// Assinatura (P0.7). Espelho no app da função has_access() do banco — a tela usa para
// desativar botões; quem bloqueia de verdade é o banco (RLS). Sem teste grátis desde
// 28/10/2026 (AC-100): a conta nova nasce com o teste já vencido.

export interface Subscription {
  user_id: string;
  trial_ends_at: string; // ISO
  status: string; // espelho do Stripe, ou 'trialing_free' durante o teste sem cartão
  current_period_end: string | null;
  stripe_customer_id: string | null;
  /** preenchido pelo webhook na primeira assinatura e mantido depois de cancelar */
  stripe_subscription_id: string | null;
  /** plano assinado, gravado pelo webhook (AC-239); nulo enquanto o Stripe não informou */
  plan?: Plan | null;
}

export const ACCESS_STATUSES = ['active', 'trialing', 'past_due'] as const;

/** Só a assinatura paga libera (AC-106): sem teste grátis, trial_ends_at não conta mais. */
export function hasAccess(sub: Subscription | null, _now?: number): boolean {
  return isPaying(sub);
}

export function isPaying(sub: Subscription | null): boolean {
  return !!sub && (ACCESS_STATUSES as readonly string[]).includes(sub.status);
}

/** Nunca assinou: abre direto nos planos (AC-207) e tem o desconto da 1ª assinatura (AC-232). */
export function neverSubscribed(sub: Subscription | null): boolean {
  return !sub?.stripe_subscription_id;
}

/** Já assinou e a assinatura acabou (cancelada ou sem pagamento): pop-up do funil (AC-208). */
export function subscriptionEnded(sub: Subscription | null, now: number): boolean {
  return !!sub && !neverSubscribed(sub) && !hasAccess(sub, now);
}

/** Garantia de reembolso: 7 dias (CDC) e 14 na UE, pela moeda dos planos (ASM-131). */
export function guaranteeDays(currency: Currency): number {
  return currency === 'eur' ? 14 : 7;
}

export type Currency = 'usd' | 'eur' | 'brl';

/** Mensal ou anual (o mensal vem marcado — AC-209). */
export type Plan = 'monthly' | 'annual';
export const PLANS: readonly Plan[] = ['monthly', 'annual'];

/** Preços da tela de planos: os do Stripe, na moeda que o Stripe Checkout vai mostrar (AC-110 / AC-111). */
export interface Pricing {
  currency: Currency;
  monthly: number;
  annual: number;
  /** localização (rede/IP) · região do aparelho · valores da spec (sem resposta do Stripe) */
  from: 'location' | 'device' | 'spec';
}

/**
 * Valores da spec (US$ 12 · € 9 · R$ 22 por mês) e do anual, cerca de 17% abaixo de 12 mensais
 * (decisão do usuário em 03/11/2026, ASM-101): só a reserva quando o Stripe não responde — quem
 * manda é o Stripe.
 */
export const SPEC_PRICES: Record<Currency, { monthly: number; annual: number }> = {
  usd: { monthly: 12, annual: 119 },
  eur: { monthly: 9, annual: 90 },
  brl: { monthly: 22, annual: 219 },
};

export function specPricing(currency: Currency): Pricing {
  return { currency, ...SPEC_PRICES[currency], from: 'spec' };
}

const SYMBOL: Record<Currency, string> = { usd: 'US$', eur: '€', brl: 'R$' };

/** "US$ 12", "€ 8,33", "R$ 15,42" — centavos só quando houver. */
export function priceLabel(amount: number, currency: Currency): string {
  const cents = Math.round(amount * 100) % 100 !== 0;
  const text = cents ? amount.toFixed(2) : String(Math.round(amount));
  return `${SYMBOL[currency]} ${currency === 'usd' ? text : text.replace('.', ',')}`;
}

/** Quanto o anual dá por mês. */
export function annualPerMonth(pricing: Pricing): number {
  return Math.round((pricing.annual / 12) * 100) / 100;
}

/** Desconto do anual sobre 12 mensais, em porcentagem inteira para o selo (≈17% → "-17%"). */
export function annualDiscount(pricing: Pricing): number {
  if (!pricing.monthly) return 0;
  return Math.round((1 - pricing.annual / (pricing.monthly * 12)) * 100);
}

const EURO_REGIONS = new Set([
  'AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
]);
const EURO_LANGS = new Set(['de', 'fr', 'it', 'nl', 'fi', 'el', 'sk', 'sl', 'et', 'lv', 'lt', 'mt', 'ga', 'hr', 'es']);

/**
 * Moeda mostrada antes do checkout, pelo idioma/região do navegador (ASM-050):
 * Brasil → R$, países do euro → €, demais → US$. O valor cobrado é o do Stripe Checkout.
 */
export function currencyFor(locales: readonly string[]): Currency {
  const first = locales[0] ?? 'en-US';
  const [lang, region] = first.replace('_', '-').split('-');
  const reg = region?.toUpperCase();
  if (reg === 'BR' || (!reg && lang.toLowerCase() === 'pt')) return 'brl';
  if (reg && EURO_REGIONS.has(reg)) return 'eur';
  if (!reg && EURO_LANGS.has(lang.toLowerCase())) return 'eur';
  return 'usd';
}
